import { ClearOutlined, SearchOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  List,
  Radio,
  Row,
  Space,
  useForm,
} from "@pankod/refine-antd";
import {
  HttpError,
  IResourceComponentsProps,
  useMutation,
  useNotification,
} from "@pankod/refine-core";
import { DefaultMaskedInput } from "components/extra/default-masked-input";
import dayjs from "dayjs";
import { ADMIN_BFF_URL } from "environment";
import { useState } from "react";
import { exportText } from "utils/exports";

interface LogsNavigationForm {
  cpf?: string;
  phone?: string;
  start_at: Date;
  end_at: Date;
}

const normalizeFilters = (values: LogsNavigationForm) => ({
  cpf: values.cpf ? values.cpf?.replace(/\D/g, "") : null,
  phone: values.phone ? values.phone?.replace(/\D/g, "") : null,
  start_at: dayjs(values.start_at).format("YYYY-MM-DD"),
  end_at: dayjs(values.end_at).format("YYYY-MM-DD"),
});

export const LogsNavigationList: React.FC<IResourceComponentsProps> = () => {
  const [searchKind, setSearchKind] = useState<"cpf" | "phone">("cpf");
  const { formProps } = useForm<{}, HttpError, LogsNavigationForm>();
  const { open } = useNotification();

  const { mutate: exportLogs, isLoading } = useMutation({
    mutationFn: exportText,
  });

  const handleCleanFilters = () => {
    formProps.form?.resetFields();
  };

  const handleGenerateLogs = (values: LogsNavigationForm) => {
    const payload = normalizeFilters(values);

    exportLogs(
      {
        url: ADMIN_BFF_URL + "/v1/navigation-logs/relatorio/",
        filenamePrefix: `logs-navegacao_${payload.cpf || payload.phone}`,
        params: payload,
      },
      {
        onSuccess: () => {
          formProps.form?.resetFields();
          open?.({
            type: "success",
            message: "Sucesso",
            description: "Arquivo gerado com sucesso",
          });
        },
        onError: (error: any) => {
          open?.({
            type: "error",
            message:
              error?.message ||
              "Ocorreu um erro ao gerar os logs de navegação.",
          });
        },
      }
    );
  };

  return (
    <List title="Logs de Navegação">
      <Card>
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <Radio.Group
            value={searchKind}
            onChange={(e) => {
              setSearchKind(e.target.value);
              formProps.form?.resetFields();
            }}
          >
            <Radio value="cpf">CPF do Cliente</Radio>
            <Radio value="phone">Telefone</Radio>
          </Radio.Group>

          <Form {...formProps} layout="vertical" onFinish={handleGenerateLogs}>
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item
                  label="CPF do Cliente:"
                  name="cpf"
                  hidden={searchKind !== "cpf"}
                  rules={[
                    {
                      required: searchKind === "cpf",
                      message: "Digite o CPF do cliente",
                    },
                  ]}
                >
                  <DefaultMaskedInput
                    mask="000.000.000-00"
                    className="input-custom ant-input"
                    placeholder="Digite o CPF"
                  />
                </Form.Item>

                <Form.Item
                  name="phone"
                  label="Telefone"
                  hidden={searchKind !== "phone"}
                  rules={[
                    {
                      min: 15,
                      message: "Telefone inválido",
                      required: searchKind === "phone",
                    },
                  ]}
                >
                  <DefaultMaskedInput
                    mask="(00) 00000-0000"
                    className="input-custom ant-input"
                    placeholder="Telefone"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={6}>
                <Form.Item name="start_at" label="Início">
                  <DatePicker placeholder="00/00/0000" format="DD/MM/YYYY" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={6}>
                <Form.Item name="end_at" label="Fim">
                  <DatePicker placeholder="00/00/0000" format="DD/MM/YYYY" />
                </Form.Item>
              </Col>

              <Col xs={24} style={{ textAlign: "right", marginTop: 8 }}>
                <Space>
                  <Button
                    type="default"
                    icon={<ClearOutlined />}
                    onClick={handleCleanFilters}
                  >
                    Limpar
                  </Button>

                  <Button
                    type="primary"
                    icon={<SearchOutlined />}
                    htmlType="submit"
                    loading={isLoading}
                    disabled={isLoading}
                  >
                    Buscar
                  </Button>
                </Space>
              </Col>
            </Row>
          </Form>
        </Space>
      </Card>
    </List>
  );
};
